import {
  isEndpointAllowed,
  isOriginTrusted,
} from '@de/config/allowed-data-sources';
import {
  DataSourceHttpError,
  DataSourceEndpointNotAllowedError,
  DataSourceInferenceNotConfiguredError,
  type InferenceDataSourceDeclaration,
} from '@de/providers/DataSourceManager.types';
import { interpolateObject } from '@de/utils/interpolation';
import { extractResponsePath } from './extractResponsePath';
import {
  BASE_BACKOFF_MS,
  type FetchDataSourceOptions,
  type FetchDataSourceResult,
  type InterpolatedRequest,
} from './fetchDataSource';

/** Generation-appropriate default; overridable via `errorStrategy.timeout`. */
export const DEFAULT_INFERENCE_TIMEOUT_MS = 30_000;

/** Cost-safe default — exactly one upstream attempt unless overridden. */
export const DEFAULT_INFERENCE_RETRIES = 0;

export interface FetchInferenceOptions extends FetchDataSourceOptions {
  readonly inferenceUrl?: string;
}

/**
 * Shape an inference declaration into the request this module will send.
 *
 * Exported so the cache key derives from the same shaping rather than from a
 * parallel copy. Headers participate in request identity for this type — two
 * declarations differing only by a tenant or auth header must not share a
 * cached generation — and the copy this replaced built them without
 * `content-type`, so the key described a request that was never sent.
 */
export function buildInferenceRequest(
  decl: InferenceDataSourceDeclaration,
  interpolationContext: unknown,
  inferenceUrl: string | undefined,
): InterpolatedRequest {
  const interpolatedHeaders = decl.headers
    ? (interpolateObject(decl.headers, interpolationContext) as Record<
        string,
        string
      >)
    : undefined;

  return {
    endpoint: inferenceUrl?.trim() ?? '',
    method: 'POST',
    headers: {
      ...(interpolatedHeaders ?? {}),
      'content-type': 'application/json',
    },
    body:
      decl.body !== undefined
        ? interpolateObject(decl.body, interpolationContext)
        : undefined,
  };
}

/**
 * Inference-kind fetch path. The retry/backoff loop intentionally mirrors
 * fetchReportingModelDataSource rather than sharing a helper — kind fetchers
 * stay independently auditable. Do not DRY them without revisiting both call
 * sites.
 *
 * The POST body is opaque + interpolated (A6). This module must not import
 * reporting vocabulary or `reporting-query-types`.
 */
export async function fetchInferenceDataSource(
  decl: InferenceDataSourceDeclaration,
  interpolationContext: unknown,
  options: FetchInferenceOptions = {},
): Promise<FetchDataSourceResult> {
  const inferenceUrl = options.inferenceUrl?.trim();
  if (!inferenceUrl) {
    return {
      status: 'error',
      request: { endpoint: '', method: 'POST' },
      error: new DataSourceInferenceNotConfiguredError(),
    };
  }

  const built = options.preparedRequest
    ? options.preparedRequest
    : buildInferenceRequest(decl, interpolationContext, inferenceUrl);
  const request =
    options.preparedRequest || !options.coerceRequestBody
      ? built
      : options.coerceRequestBody(built);

  if (!isEndpointAllowed(request.endpoint, request.method, options.tenantId)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[DataSourceManager] blocked inference endpoint by allowlist: ${request.method} ${request.endpoint}`,
    );
    return {
      status: 'error',
      request,
      error: new DataSourceEndpointNotAllowedError(
        request.endpoint,
        request.method,
      ),
    };
  }

  if (!isOriginTrusted(request.endpoint, options.trustedOrigins)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[DataSourceManager] blocked inference endpoint by origin allowlist: ${request.method} ${request.endpoint}`,
    );
    return {
      status: 'error',
      request,
      error: new DataSourceEndpointNotAllowedError(
        request.endpoint,
        request.method,
      ),
    };
  }

  const timeout = decl.errorStrategy?.timeout ?? DEFAULT_INFERENCE_TIMEOUT_MS;
  const retries = decl.errorStrategy?.retries ?? DEFAULT_INFERENCE_RETRIES;
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const delay = options.delay ?? defaultDelay;

  let lastError: Error = new Error(
    'Inference fetch never attempted — unreachable.',
  );

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (options.signal?.aborted) {
      return {
        status: 'error',
        request,
        error: toError(options.signal.reason, 'Inference fetch aborted.'),
      };
    }

    try {
      const response = await fetchOnce(
        request,
        fetchImpl,
        timeout,
        options.signal,
        options.setTimeoutImpl,
        options.clearTimeoutImpl,
      );

      if (response.status >= 400 && response.status < 500) {
        return {
          status: 'error',
          request,
          error: new DataSourceHttpError(
            response.status,
            request.endpoint,
            request.method,
          ),
        };
      }
      if (response.status >= 500) {
        lastError = new DataSourceHttpError(
          response.status,
          request.endpoint,
          request.method,
        );
        if (attempt < retries) {
          await delay(backoffMs(attempt), options.signal);
          continue;
        }
        return { status: 'error', request, error: lastError };
      }

      const json = (await response.json()) as unknown;
      const coerced = options.coerceResponse
        ? options.coerceResponse(json, request)
        : json;
      const extracted = extractResponsePath(coerced, decl.responsePath);

      if (isEmpty(extracted)) {
        return { status: 'empty', request, data: extracted };
      }
      return { status: 'resolved', request, data: extracted };
    } catch (error) {
      lastError = toError(error, 'Inference fetch failed.');
      if (isAbortError(error) || options.signal?.aborted) {
        return { status: 'error', request, error: lastError };
      }
      if (attempt < retries) {
        try {
          await delay(backoffMs(attempt), options.signal);
        } catch {
          return { status: 'error', request, error: lastError };
        }
        continue;
      }
      return { status: 'error', request, error: lastError };
    }
  }

  return { status: 'error', request, error: lastError };
}

async function fetchOnce(
  request: InterpolatedRequest,
  fetchImpl: typeof fetch,
  timeout: number,
  outerSignal: AbortSignal | undefined,
  setTimeoutImpl: typeof setTimeout = setTimeout,
  clearTimeoutImpl: typeof clearTimeout = clearTimeout,
): Promise<Response> {
  const controller = new AbortController();
  const onOuterAbort = (): void => {
    controller.abort(outerSignal?.reason);
  };
  if (outerSignal) {
    if (outerSignal.aborted) {
      controller.abort(outerSignal.reason);
    } else {
      outerSignal.addEventListener('abort', onOuterAbort, { once: true });
    }
  }
  const timer = setTimeoutImpl(() => {
    controller.abort(new Error('Inference fetch timed out.'));
  }, timeout);

  try {
    const init: RequestInit = {
      method: request.method,
      headers: request.headers,
      signal: controller.signal,
    };
    if (request.body !== undefined) {
      init.body = JSON.stringify(request.body);
    }
    return await fetchImpl(request.endpoint, init);
  } finally {
    clearTimeoutImpl(timer);
    if (outerSignal) {
      outerSignal.removeEventListener('abort', onOuterAbort);
    }
  }
}

function backoffMs(attempt: number): number {
  return BASE_BACKOFF_MS * 2 ** attempt;
}

function defaultDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(toError(signal.reason, 'Inference fetch aborted during backoff.'));
      return;
    }
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      cleanup();
      reject(
        toError(signal?.reason, 'Inference fetch aborted during backoff.'),
      );
    };
    const cleanup = (): void => {
      signal?.removeEventListener('abort', onAbort);
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length === 0;
  }
  return false;
}

function isAbortError(error: unknown): boolean {
  if (error === null || typeof error !== 'object') {
    return false;
  }
  const name = (error as { name?: unknown }).name;
  return name === 'AbortError';
}

function toError(value: unknown, fallback: string): Error {
  if (value instanceof Error) {
    return value;
  }
  if (typeof value === 'string') {
    return new Error(value);
  }
  return new Error(fallback);
}
